import { CollisionSystem, HistoryApi, ModelLoadedEventSystem, PlannerCoreApi, SelectionEventSystem, MeasurementsWidgetSystem, HighlightAddedEntitySystem, UnavailableProductsEventSystem, HistoryEventSystem, InvalidFramePlacementSystem } from '@inter-ikea-storageone/range';
import { FunctionalMarginSystem, FunctionalMarginValidation, PlacementZoneValidation } from '@inter-ikea-core/core';
import { BoaxelCameraApi } from './BoaxelCameraApi';
import { BoaxelEntityApi } from './BoaxelEntityApi';
import { BoaxelProductApi } from './BoaxelProductApi';
import { BoaxelSceneApi } from './BoaxelSceneApi';
import Boaxel from '@inter-ikea-storagetwo/boaxel/dist';
import { PopulateUprightSystem } from './systems/PopulateUprightSystem';
import { OptimizeMountingRailsSystem } from './systems/OptimizeMountingRailsSystem';
import { AddInvisibleAdjustableEntitySystem } from './systems/AddInvisibleAdjustableEntitySystem';
import { BoaxelDistanceMeasurementSystem } from './systems/BoaxelDistanceMeasurementSystem';
import { NonMountingRailInfoSystem } from './systems/NonMountingRailInfoSystem';
import { AdjustableSectionValidation } from './validations/AdjustableSectionValidation';
export class BoaxelCoreApi extends PlannerCoreApi {
    constructor() {
        super(...arguments);
        this.BoaxelApi = new Boaxel({ core: this.core });
    }
    bootstrap(config) {
        if (!this.core) {
            throw new Error('Core not initialized yet');
        }
        // Setup core base functionality
        const { selectionService, placementService } = super.initializeBaseCoreFunctionality();
        // Register core systems
        this.registerSystem(new FunctionalMarginSystem());
        // Register core validations
        this.registerValidation(new FunctionalMarginValidation(false, this.core.services.entityManager));
        this.registerValidation(new PlacementZoneValidation());
        this.registerValidation(new AdjustableSectionValidation(this));
        // Setup plannner APIs
        this.products = new BoaxelProductApi(this.core.services, placementService, this.reportErrorToSentry);
        this.entities = new BoaxelEntityApi(selectionService, this.core, placementService, this.ipexViewer, this.reportErrorToSentry);
        // Register planner systems
        this.registerSystem(new HistoryEventSystem(this));
        this.registerSystem(new UnavailableProductsEventSystem(this));
        this.registerSystem(new ModelLoadedEventSystem(this));
        this.registerSystem(new InvalidFramePlacementSystem(this));
        this.registerSystem(new CollisionSystem(this, []));
        this.registerSystem(new SelectionEventSystem(this));
        this.registerSystem(new HighlightAddedEntitySystem(this));
        this.registerSystem(new PopulateUprightSystem(this));
        this.registerSystem(new OptimizeMountingRailsSystem(this));
        this.registerSystem(new AddInvisibleAdjustableEntitySystem(this));
        this.registerSystem(new BoaxelDistanceMeasurementSystem(this));
        this.registerSystem(new NonMountingRailInfoSystem(this));
        return this.loadProductCatalogs(config.productCatalogs);
    }
    getMainProductCatalog() {
        return this.catalogs['boaxel'];
    }
    initSceneApi() {
        this.scene = new BoaxelSceneApi(this.core, this.ipexViewer, this.reportErrorToSentry);
        this.registerSystem(new MeasurementsWidgetSystem(this));
    }
    initCameraApi(canvas) {
        this.camera = new BoaxelCameraApi(this.core.services.sceneManager, this.ipexViewer, this.core.services.entityManager, this, canvas);
    }
    indexMountingRailsInSolution(mountingRail) {
        if (!this.BoaxelApi)
            throw new Error('indexMountingRailsInSolution called too early.');
        return this.BoaxelApi.indexMountingRailsInSolution(mountingRail);
    }
    getMountingRailIndexInSolution(mountingRail) {
        if (!this.BoaxelApi)
            throw new Error('getMountingRailIndexInSolution called too early.');
        return this.BoaxelApi.getMountingRailIndexInSolution(mountingRail);
    }
    calculateOptimalMountingRailSetupForAllSolutions() {
        if (!this.BoaxelApi)
            throw new Error('calculateOptimalMountingRailSetupForAllSolutions called too early.');
        return this.BoaxelApi.calculateOptimalMountingRailSetupForAllSolutions();
    }
    recolorSolution(color, mountingRails) {
        if (!this.BoaxelApi)
            throw new Error('recolorSolution called too early.');
        return this.BoaxelApi.recolorSolution(color, mountingRails);
    }
    /**
     * @override
     */
    async loadCatalog(productCatalog) {
        if (productCatalog.name === 'boaxel') {
            this.history = new HistoryApi(this);
            this.catalogs['boaxel'] = this.BoaxelApi.setupCatalog();
        }
        else {
            await super.loadCatalog(productCatalog);
        }
    }
    /**
     *
     * @override
     */
    async loadScene(config, setRoom) {
        // Wait for the scene to load before loading the upright system
        this.getSystem('PopulateUprightSystem')?.disable();
        super.loadScene(config, setRoom);
        // When the scene has been loaded we can enable the upright system again
        this.getSystem('PopulateUprightSystem')?.enable();
    }
    /**
     * @override
     */
    createViewer(canvas, renderSettings, applicationName, locale, dexfBaseUrl, dexfApiKey, isProduction, enableFeatures, roomOptions) {
        const viewer = super.createViewer(canvas, renderSettings, applicationName, locale, dexfBaseUrl, dexfApiKey, isProduction, enableFeatures, roomOptions);
        if (!this.BoaxelApi)
            throw new Error('Not able to register viewer.');
        this.BoaxelApi.init({
            viewer: this.ipexViewer,
            viewerCoreAdapter: this.viewer
        });
        return viewer;
    }
}
//# sourceMappingURL=BoaxelCoreApi.js.map